import { useEffect, useState } from "react";

/**
 * The route being prerendered. Set by the build-time prerender pass (there is no
 * `window.location` to read there); ignored entirely in the browser.
 */
let ssrPathname = "/";

export function setSsrPathname(pathname: string) {
  ssrPathname = pathname;
}

export function getPathname() {
  if (typeof window === "undefined") {
    return ssrPathname;
  }

  return window.location.pathname || "/";
}

export function getSearch() {
  if (typeof window === "undefined") {
    return "";
  }

  return window.location.search || "";
}

/**
 * Immediately commits a navigation (history push + popstate broadcast).
 * Used by the page-transition curtain after its cover animation finishes.
 */
export function commitNavigation(to: string) {
  if (typeof window === "undefined") {
    return;
  }

  window.history.pushState({}, "", to);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function navigate(to: string) {
  if (typeof window === "undefined") {
    return;
  }

  if (to === `${window.location.pathname}${window.location.search}${window.location.hash}`) {
    return;
  }

  // Give the cinematic route curtain a chance to take over. If a listener
  // calls preventDefault, it is responsible for calling commitNavigation.
  const event = new CustomEvent("cw:navigate", { detail: { to }, cancelable: true });
  const proceed = window.dispatchEvent(event);

  if (proceed) {
    commitNavigation(to);
  }
}

export function usePathname() {
  const [pathname, setPathname] = useState(getPathname);

  useEffect(() => {
    const onPopState = () => setPathname(getPathname());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  return pathname;
}

export function useSearch() {
  const [search, setSearch] = useState(getSearch);

  useEffect(() => {
    const onPopState = () => setSearch(getSearch());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  return search;
}
